import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import {
    CarouselButtons,
    CarouselContainer,
    CarouselCounter,
    CarouselCountLabel,
    CarouselItem,
    CarouselWrapper,
    CounterItem,
} from './carousel-images.styles'
import { SaveAdvert } from '../search-listing-save-advert/save-advert'
import { IconPhotograph } from '@at/design-system-icons'
import { useRetailerStockSearch } from '../../utils/retailer-stock'
import { ChevronLeft, ChevronRight } from '../icons/icons'
import { useAppContext } from '@at/sauron-platform-core'
import { ListingType } from '../../__generated__/fed-graphql-types'
import { SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_trackingContext_TrackingContext } from '../../contexts/search-results-context/query/__generated__/search-results-listings.fed.query'
import { useATTrackInteraction } from '@at/consumer-web-snowplow'
import { useJourneyDataBuilder } from '../../hooks/use-journey-data-builder'
import { AdvertImageWithBlur } from '../advert-image-with-blur/advert-image-with-blur'
import { getCommonComponentContexts, TrackingContext } from '../../utils/tracking/tracking'
import { calculateGridPosition } from '../../utils/tracking/grid-position'
import { useMobileCarouselWithFourImages } from '../../hooks/use-abtest.hook'
import {
    ButtonOnImageContainer,
    OverlayDiv,
    SeeAllImagesButton,
} from '../search-listing-image-section/search-listing-image-section.styles'

interface CarouselTypes {
    images: ReadonlyArray<string | null>
    title: string
    advertId: string
    saveAdvertIsSaved: boolean
    isLeasing?: boolean
    badge?: ReactNode
    numberOfImages: number
    fpaUrl: string
    type?: ListingType
    trackingContext?: SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_trackingContext_TrackingContext
    listingRef?: HTMLDivElement
    initialSlide?: number
}

const MIN_IMAGES_TO_SHOW = 2
export const CarouselImages = ({
    images,
    title,
    advertId,
    saveAdvertIsSaved,
    isLeasing,
    badge,
    numberOfImages,
    fpaUrl,
    type,
    trackingContext,
    listingRef,
    initialSlide = 0,
}: CarouselTypes) => {
    const [imagesToShow, setImagesToShow] = useState(MIN_IMAGES_TO_SHOW)
    const [currentIndex, setCurrentIndex] = useState(initialSlide)
    const [isRetailerStockSearch] = useRetailerStockSearch()
    const { isDesktop } = useAppContext()
    const isMobile = !isDesktop()
    const isMobileCarouselWithFourImages = useMobileCarouselWithFourImages()
    const isDesktopOrMobileCarousel = isDesktop() || isMobileCarouselWithFourImages
    const totalImages = images.length
    const isMobileSingleImage = isMobile && !isMobileCarouselWithFourImages
    const carouselRef = useRef<HTMLDivElement>(null)

    const commonComponentContexts = getCommonComponentContexts({
        ...trackingContext,
        gridPosition: calculateGridPosition(listingRef, trackingContext?.card.position),
    } as TrackingContext)

    const carouselImageViewedWithTrackingHandler = useATTrackInteraction()
    const trackingButtonInteraction = useATTrackInteraction()

    const hideLeft = currentIndex === 0
    const hideRight = currentIndex === totalImages - 1

    const { journeyDataBuilder } = useJourneyDataBuilder({
        trackingContext,
        ref: listingRef,
        type,
        advertId,
    })

    const scrollToSlide = useCallback((index: number, behavior: globalThis.ScrollBehavior = 'smooth') => {
        const carouselChildren = carouselRef?.current?.children[index] as HTMLElement
        if (carouselChildren && carouselRef?.current) {
            const offsetLeft = carouselChildren.offsetLeft
            carouselRef?.current?.scrollTo({
                left: offsetLeft,
                behavior: behavior,
            })
        }
    }, [])

    const previousSlide = useCallback(() => {
        if (currentIndex - 1 > -1) {
            scrollToSlide(currentIndex - 1)
        }
    }, [currentIndex, scrollToSlide])

    const nextSlide = useCallback(() => {
        if (currentIndex + 1 < totalImages) {
            scrollToSlide(currentIndex + 1)
        }
    }, [currentIndex, scrollToSlide, totalImages])

    const linkProps = {
        href: fpaUrl,
        onClick: journeyDataBuilder,
    }

    const canSaveAdvert = !isRetailerStockSearch && !isLeasing

    useEffect(() => {
        let timeout: NodeJS.Timeout

        const handleScroll = () => {
            if (carouselRef.current) {
                const carouselChildren = [...carouselRef.current.children]

                const index = carouselChildren.findIndex((child) => {
                    const rectLeft = child.getBoundingClientRect()?.left
                    if (carouselRef.current) {
                        const containerRectLeft = carouselRef?.current?.getBoundingClientRect()?.left
                        return rectLeft >= containerRectLeft - 150
                    }
                    return
                })

                clearTimeout(timeout)
                if (index !== -1) {
                    setCurrentIndex(index)
                    setImagesToShow((prevState) =>
                        index + MIN_IMAGES_TO_SHOW > prevState ? index + MIN_IMAGES_TO_SHOW : prevState
                    )

                    timeout = setTimeout(() => {
                        carouselImageViewedWithTrackingHandler({
                            trackingLabel: `carousel_image_${index + 1}_viewed`,
                            customiseEntities: (entities) => {
                                return [...entities, ...commonComponentContexts]
                            },
                        })
                    }, 150)
                }
            }
        }

        const carousel = carouselRef.current
        carousel?.addEventListener('scroll', handleScroll, {
            passive: true,
        })

        return () => {
            carousel?.removeEventListener('scroll', handleScroll)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalImages])

    if (totalImages === 0) {
        return (
            <CarouselContainer data-testid="carousel-images">
                <CarouselItem {...linkProps} aria-label="image not available" data-testid="carousel-image-1">
                    <AdvertImageWithBlur src={''} title={title} index={0} hideImage={false} />
                </CarouselItem>
                {canSaveAdvert ? (
                    <SaveAdvert advertId={advertId} isSaved={saveAdvertIsSaved} trackingContext={trackingContext} />
                ) : null}
            </CarouselContainer>
        )
    }

    const carouselImgCount = `${currentIndex + 1}/${numberOfImages}`

    const CarouselCount = () => {
        if (isMobileSingleImage) {
            return (
                <CarouselCountLabel {...linkProps} $testBucket aria-label="Number of images">
                    <IconPhotograph data-testid="photograph-icon" title="" viewBox="-2 -2 32 32" />
                    {numberOfImages}
                </CarouselCountLabel>
            )
        } else {
            return (
                <CarouselCountLabel
                    {...linkProps}
                    $testBucket
                    aria-label={`image ${currentIndex + 1} of ${numberOfImages} total`}
                >
                    {carouselImgCount}
                </CarouselCountLabel>
            )
        }
    }

    const getCarouselFinalImageContainer = () => {
        const isLastImage = currentIndex === 3
        if (((isMobile && isMobileCarouselWithFourImages) || isDesktop()) && isLastImage) {
            return (
                <ButtonOnImageContainer>
                    <OverlayDiv as={'a'} {...linkProps}></OverlayDiv>
                    <SeeAllImagesButton
                        displayType={'primary'}
                        text="See all images"
                        url={fpaUrl}
                        onInteraction={() =>
                            trackingButtonInteraction({
                                trackingLabel: 'see_all_images',
                                customiseEntities: (entities) => {
                                    return [...entities, ...commonComponentContexts]
                                },
                            })
                        }
                    />
                </ButtonOnImageContainer>
            )
        }
    }

    return (
        <CarouselWrapper>
            {badge}
            <CarouselContainer ref={carouselRef} data-testid="carousel-images">
                {images.map((image, index) => {
                    const hideImage = index + 1 > imagesToShow
                    return (
                        <CarouselItem
                            {...linkProps}
                            key={`${image}${String(index)}`}
                            aria-label={isMobile ? 'Car advert image' : `image ${index + 1} of ${totalImages}`}
                            data-testid={`carousel-image-${index + 1}`}
                            tabIndex={-1}
                        >
                            <AdvertImageWithBlur src={image ?? ''} title={title} index={index} hideImage={hideImage} />
                        </CarouselItem>
                    )
                })}
                {getCarouselFinalImageContainer()}

                {canSaveAdvert ? (
                    <SaveAdvert advertId={advertId} isSaved={saveAdvertIsSaved} trackingContext={trackingContext} />
                ) : null}
                {!isMobile ? (
                    <div className="carousel-button-wrapper">
                        <CarouselButtons
                            onClick={previousSlide}
                            isLeftButton
                            hideLeftArrow={hideLeft}
                            id="carousel-left-toggle"
                            data-testid="carousel-left-toggle"
                            aria-hidden={hideLeft ? 'true' : 'false'}
                        >
                            <ChevronLeft name="Toggle pictures left" size="medium" />
                        </CarouselButtons>

                        <CarouselButtons
                            onClick={nextSlide}
                            hideRightArrow={hideRight}
                            id="carousel-right-toggle"
                            data-testid="carousel-right-toggle"
                            aria-hidden={hideRight ? 'true' : 'false'}
                        >
                            <ChevronRight name="Toggle pictures right" size="medium" />
                        </CarouselButtons>
                    </div>
                ) : null}
                <CarouselCount />
            </CarouselContainer>
            {isDesktopOrMobileCarousel ? (
                <CarouselCounter data-testid="carousel-count-item">
                    {images.map((image, index) => (
                        <CounterItem key={`${image}${String(index)}`} isSelected={index === currentIndex} />
                    ))}
                </CarouselCounter>
            ) : null}
        </CarouselWrapper>
    )
}
